import { Role, SlimUser } from './userApi';
import { PagedItems } from '@texas/types';
import { httpClient } from '../httpClient/httpClient';
import { defaultPageSize } from '@texas/components/shared/dataTable/defaultTableOptions';

export interface Contact {
  id: number;
  name: string;
  email: string;
  archived?: string | null;
  customers: Customer[];
  user?: SimpleContact | null;
}

export interface LinkedUserEntity {
  id: number;
  name: string;
  archived: boolean;
}

export interface ExistingSupplierUser extends SlimUser {
  customers: LinkedUserEntity[];
  suppliers: LinkedUserEntity[];
  articles: LinkedUserEntity[];
  role: Role | null;
  archived: boolean;
  canLink: boolean;
}

export interface SimpleContact {
  id: number;
  name: string;
  email: string;
}

export interface Customer {
  id: number;
  name: string;
  identifier: string;
  archived: boolean;
}

export interface ContactListItem {
  id: number;
  name: string;
  email: string;
  archived?: string | null;
  customerIds: number[];
  user?: SimpleContact | null;
}

export interface SearchQuery {
  sortBy: string;
  sortDesc: boolean;
  searchTerm: string;
  page: number;
  pageSize: number;
  includeArchived: boolean;
  customerId: number | null;
  supplierId: number | null;
}

export const defaultSearchQuery: SearchQuery = {
  sortBy: 'name',
  sortDesc: false,
  searchTerm: '',
  page: 1,
  pageSize: defaultPageSize,
  includeArchived: false,
  customerId: null,
  supplierId: null,
};

export enum ContactAddToType {
  Customer = 1,
  Supplier = 2,
}

export interface CreateContactLoginResult {
  contact: Contact;
  welcomeEmailFailed: boolean;
}

export interface CreateContactRequest {
  name: string;
  email: string;
  referenceId: number;
  addTo: ContactAddToType;
  userId?: number;
}

export interface UpdateContactRequest {
  name: string;
  email: string;
  userId?: number;
}

export interface ConnectContactRequest {
  id: number;
  referenceId: number;
}

export const contactsApi = {
  createWithLogin: (
    request: CreateContactRequest & { sendWelcomeMail: boolean },
  ) =>
    httpClient.post<CreateContactLoginResult>('contacts/with-login', {
      data: request,
    }),
  getExistingSupplierUser: async (id: number, email: string) => {
    const response = await httpClient.get<ExistingSupplierUser | null>(
      `contacts/${id}/existing-supplier-user`,
      { params: { email } },
    );
    if (!response.hasError && response.status === 204) {
      return { ...response, data: null };
    }
    return response;
  },
  linkSupplierUser: (id: number, userId: number, email: string) =>
    httpClient.put<SlimUser>(`contacts/${id}/supplier-user`, {
      data: { userId, email },
    }),
  get: (id: number) => httpClient.get<Contact>(`contacts/${id}`),
  getVariantContact: (id: number) =>
    httpClient.get<Contact>(`contacts/variant/${id}`),
  getAll: (params: SearchQuery) =>
    httpClient.get<PagedItems<ContactListItem>>(`contacts/all`, {
      params,
    }),
  getNotLinked: (params: SearchQuery) =>
    httpClient.get<PagedItems<ContactListItem>>(`contacts/not-linked`, {
      params,
    }),
  create: (request: CreateContactRequest) =>
    httpClient.post<Contact>(`contacts`, {
      data: request,
    }),
  update: (id: number, request: UpdateContactRequest) =>
    httpClient.put<Contact>(`contacts/${id}`, {
      data: request,
    }),
  connectCustomer: (request: ConnectContactRequest) =>
    httpClient.put<Contact>(`contacts/connect/customer`, { data: request }),
  disconnectCustomer: (request: ConnectContactRequest) =>
    httpClient.put<Contact>(`contacts/disconnect/customer`, { data: request }),
  connectSupplier: (request: ConnectContactRequest) =>
    httpClient.put<Contact>(`contacts/connect/supplier`, { data: request }),
  disconnectSupplier: (request: ConnectContactRequest) =>
    httpClient.put<Contact>(`contacts/disconnect/Supplier`, { data: request }),
  archive: (id: number) => httpClient.put(`contacts/${id}/archive`),
  restore: (id: number) => httpClient.put(`contacts/${id}/restore`),
};
